import { useEffect, useState } from 'react'
import { Show, UserButton, useAuth } from '@clerk/react'
import { Link } from 'react-router-dom'
import { getActivePod } from '@/lib/pod'
import StaffSignIn from '@/components/StaffSignIn'
import { isOwnerClaim } from '@/lib/staff-role'
import { DocumentsSection } from '@/components/DocumentIntake'
import StoredDocuments from '@/components/StoredDocuments'

/**
 * THE KELVO OWNER'S DOCUMENTS PAGE — /documents on a staff build (step 3c).
 *
 * Three things, top to bottom, in the order the work happens:
 *   1. Upload or paste a document (the shared DocumentIntake, as on Foredesk).
 *   2. Approve what came out of it — "waiting for your say-so". Nothing reaches
 *      staff until he signs it.
 *   3. The files kept for staff, with open and delete.
 *
 * ONE SCREEN, ONE TRUTH (G52, batch 2ab). The two lists change each other:
 * an upload or an approval changes the files kept; deleting or moving a file
 * drops or re-files its proposals. Each tells the page, and the page refreshes
 * the OTHER list, so neither shows something the engine has already changed.
 * Only a person's action tells the page, never a list's own refresh, so the
 * two cannot set each other off in a loop.
 *
 * OWNER ONLY, TWICE. This screen checks the owner claim so office and site staff
 * are told plainly it is not for them; the Worker's ownerAdminGate is what
 * actually refuses every /api/admin call from anyone else. The screen is the
 * courtesy, the Worker is the lock.
 */
export default function OwnerDocuments() {
  const pod = getActivePod()
  const { isLoaded, sessionClaims } = useAuth()
  // Bumped by the intake whenever what is held may have changed, so the kept
  // files list beneath it refreshes.
  const [filesKey, setFilesKey] = useState(0)
  // Bumped by the kept files list after a delete or a move, so the waiting
  // list above it refreshes (G52).
  const [waitingKey, setWaitingKey] = useState(0)

  useEffect(() => {
    const previous = document.title
    document.title = `${pod.brandName} — documents`
    return () => {
      document.title = previous
    }
  }, [pod.brandName])

  if (!isLoaded) {
    return (
      <div className="flex min-h-[100svh] items-center justify-center bg-surface-base">
        <p className="text-sm text-text-muted" role="status">
          Loading…
        </p>
      </div>
    )
  }

  return (
    <>
      <Show when="signed-out">
        <StaffSignIn returnTo="/documents" />
      </Show>

      <Show when="signed-in">
        <div className="min-h-[100svh] bg-surface-base">
          <header className="border-b border-border-subtle bg-surface-raised">
            <div className="mx-auto flex max-w-3xl items-center justify-between gap-4 px-4 py-3">
              <div className="min-w-0">
                <p className="font-display font-semibold leading-tight tracking-tight truncate">
                  {pod.brandName}
                </p>
                <p className="text-xs text-text-muted leading-tight">Documents</p>
              </div>
              <div className="flex items-center gap-3">
                <Link
                  to="/"
                  className="rounded-lg border border-border-subtle px-3 py-1.5 text-sm font-medium text-text-primary transition-colors hover:border-brand-accent focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-accent focus-visible:ring-offset-2"
                >
                  Back to {pod.assistantName}
                </Link>
                <UserButton />
              </div>
            </div>
          </header>

          <main className="mx-auto max-w-3xl px-4 py-8">
            {isOwnerClaim(sessionClaims) ? (
              <>
                <DocumentsSection
                  intro={
                    <>
                      Upload the paperwork {pod.assistantName} should answer from — a procedure,
                      a RAMS, a method statement, a template. It pulls out the facts for you to
                      check, and nothing reaches your staff until you approve it.
                    </>
                  }
                  onChanged={() => setFilesKey((k) => k + 1)}
                  refreshKey={waitingKey}
                  drawers
                />
                <StoredDocuments
                  refreshKey={filesKey}
                  onChanged={() => setWaitingKey((k) => k + 1)}
                />
              </>
            ) : (
              <section className="rounded-card border border-border-subtle bg-surface-raised p-6">
                <h1 className="font-display text-lg font-semibold tracking-tight">
                  This page is for the owner
                </h1>
                <p className="mt-2 text-sm text-text-muted">
                  Adding documents is done by the owner of {pod.brandName}. If you think you should
                  have access, speak to the office.
                </p>
              </section>
            )}
          </main>
        </div>
      </Show>
    </>
  )
}
